import { useState } from 'react'
import qs from 'qs'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useSnackbar } from 'notistack'
import { useRequestHandler } from '@/hooks/useRequestHandler'
import { skipToken } from '@reduxjs/toolkit/query'

import EndConsultationDialog, { EndConsultationDialogProps } from '@/components/ui/dialogs/EndConsultationDialog'

import { useEndConsultationMutation, useGetConsultationQuery } from '@/core/services/consultation'

import { useIsWebView } from '@/hooks/useSizeBreakpoints'

import CreateConsultationDialog from '@/pages/create-consultation/CreateConsultationDialog'
import ExpertiseUpdateDialog from '@/pages/expertise/ExpertiseUpdateDialog'

import { useAppSelector } from '@/redux/rootReducer'
import { selectAuthUser } from '@/redux/reducers/auth.reducer'
import { createExpertise, selectExpertiseCreateRequestTracker, selectNewlyCreatedExpertiseId } from '@/redux/reducers/expertise.reducer'

import { getApiErrorMessage } from '@/utils/errors'
import { useStopRecordingMutation } from '@/core/api/recording'
import _ from 'lodash'
import useIsPds from '@/hooks/useIsPds'
import { useReportLicenseEnabled, useReportLicenseEnabledForUser } from '@/features/report'

export function EndLiveConsultationDialog(props: Omit<EndConsultationDialogProps, 'isEnding' | 'onEndConsultation'>) {
  const navigate = useNavigate()
  const { consultationId } = useParams<{ consultationId: string }>()
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation()
  const isWebView = useIsWebView()
  const [stopRecording] = useStopRecordingMutation()
  const [consultDialog, setConsultDialog] = useState(false)
  const [expertiseDialog, setExpertiseDialog] = useState(false)

  const authUser = useAppSelector(selectAuthUser)

  const { data: consultation } = useGetConsultationQuery(consultationId ?? skipToken)
  const hasReportLicense = useReportLicenseEnabled()
  const [endConsultation, { isLoading: isEnding }] = useEndConsultationMutation()
  const isPds = useIsPds()
  const newlyCreatedExpertiseId = useAppSelector(selectNewlyCreatedExpertiseId)
  const doctorRecordingPermission = useReportLicenseEnabledForUser(consultation?.medecin?._id ?? '')
  const canStopRecordingPermission = !doctorRecordingPermission || !isPds

  const { dispatch: generateExpertiseDispatch } = useRequestHandler({
    onSuccess: () => {
      enqueueSnackbar(t('notification.expertise.create.success'), { variant: 'success' })
      if (!isWebView) window.open(`/expertises/${newlyCreatedExpertiseId}/update`, '_blank')
      else setExpertiseDialog(true)
      props.onClose?.()
    },
    onError: () => enqueueSnackbar(t('notification.consultation.end.error'), { variant: 'error' }),
    requestTrackerSelector: selectExpertiseCreateRequestTracker,
  })

  const handleEndLiveConsultation = async () => {
    if (!consultationId) return

    try {
      const endedConsultation = await endConsultation(consultationId).unwrap()
      const userId = authUser?._id
      if (hasReportLicense && canStopRecordingPermission && userId) {
        stopRecording({ consultationId, userId }).catch((error) => {
          enqueueSnackbar(t('notification.consultation.dictaphone.error'), { variant: 'error' })
        })
      }
      const redirectTo =
        authUser?._id === endedConsultation.medecin?._id && endedConsultation.waitingRoom ? `/waiting-rooms/${endedConsultation.waitingRoom}` : '/'
      navigate(redirectTo)
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, t('notification.consultation.end.error')), { variant: 'error' })
      const redirectTo = consultation?.type === 'netsoins' ? '/netsoins/0' : '/'
      navigate(redirectTo)
    }
  }

  const handleGenerateExpertise = () => {
    if (consultationId)
      generateExpertiseDispatch(
        createExpertise({
          expertise: {
            patient: consultation?.patient._id,
            consultations: [consultationId],
          },
        })
      )
  }

  const handleOnPlan = () => {
    if (isWebView) {
      setConsultDialog(true)
    } else if (consultation)
      window.open(`/consultations/create?${qs.stringify({ patient: consultation.patient._id, doctor: consultation.medecin?._id })}`, '_blank')
  }

  return (
    <>
      <EndConsultationDialog
        {...props}
        onPlan={handleOnPlan}
        onGenerateExpertise={handleGenerateExpertise}
        isEnding={isEnding}
        onEndConsultation={handleEndLiveConsultation}
      />
      <CreateConsultationDialog open={consultDialog} onClose={() => setConsultDialog(false)} patientId={consultation?.patient._id} />
      <ExpertiseUpdateDialog expertiseId={newlyCreatedExpertiseId} open={expertiseDialog} onClose={() => setExpertiseDialog(false)} />
    </>
  )
}

export default EndLiveConsultationDialog
